import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Clock, Copy, ShieldCheck } from "lucide-react";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKCardHeader, VLKCardTitle, VLKCardContent,
  VLKButton, VLKInput, VLKLabel, VLKSelect, VLKStatusBadge, toast,
} from "@/components/vlk";
import { IDS } from "@/constants/testIds";

function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000)));
  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => setRemaining(Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000))), 500);
    return () => clearInterval(t);
  }, [expiresAt]);
  return remaining;
}

export default function QRPage() {
  const [purpose, setPurpose] = useState("identity_verification");
  const [ttl, setTtl] = useState(60);
  const [qr, setQr] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  const remaining = useCountdown(qr?.expires_at);
  const expired = qr && remaining <= 0;

  const generate = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/qr/create", { purpose, ttl: Number(ttl), one_time: true });
      setQr(data);
    } catch (e) { toast.error("Failed to generate", { description: apiErrorText(e) }); }
    finally { setGenLoading(false); }
  };

  // Verify side
  const [payload, setPayload] = useState("");
  const [signature, setSignature] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const verify = async () => {
    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post("/qr/verify", { payload, signature });
      setVerifyResult({ ok: true, data });
      toast.success("QR verified", { description: `Purpose: ${data.purpose}` });
    } catch (e) { setVerifyResult({ ok: false, error: apiErrorText(e) }); }
    finally { setVerifyLoading(false); }
  };

  const qrString = qr ? JSON.stringify({ payload: qr.payload, signature: qr.signature }) : "";

  return (
    <div>
      <VLKPageHeader
        title="Institution QR"
        description="Generate and validate short-lived HMAC-signed one-time QR tokens your institution issues (staff badges, event admission, kiosk challenges). This is distinct from Scan & Verify, which consumes the citizen's Wallet session QR."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <VLKCard>
          <VLKCardHeader><VLKCardTitle>Generate</VLKCardTitle></VLKCardHeader>
          <VLKCardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <VLKLabel>Purpose</VLKLabel>
                <VLKSelect value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                  <option value="identity_verification">identity_verification</option>
                  <option value="institution_verification">institution_verification</option>
                  <option value="one_time_scan">one_time_scan</option>
                </VLKSelect>
              </div>
              <div className="space-y-1.5">
                <VLKLabel>TTL (seconds)</VLKLabel>
                <VLKInput type="number" value={ttl} onChange={(e) => setTtl(e.target.value)} min={15} max={300} />
              </div>
            </div>
            <VLKButton data-testid={IDS.qrGenerateBtn} onClick={generate} loading={genLoading} className="mt-4 w-full">
              <QrCode className="h-4 w-4" />Generate QR
            </VLKButton>

            {qr && (
              <div className="mt-6 border border-border rounded p-6 flex flex-col items-center bg-background">
                <div data-testid={IDS.qrCanvas} className="p-3 bg-white rounded-sm">
                  <QRCodeSVG value={qrString} size={192} level="H" bgColor="#ffffff" fgColor="#0a0f1e" />
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className={"font-mono text-lg tabular " + (remaining < 10 ? "text-destructive" : remaining < 20 ? "text-warning" : "text-foreground")}>
                    {String(Math.floor(remaining / 60)).padStart(2, "0")}:{String(remaining % 60).padStart(2, "0")}
                  </span>
                  <VLKStatusBadge status={expired ? "rejected" : "active"}>{expired ? "expired" : "valid"}</VLKStatusBadge>
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Token: {qr.token_id}</div>
                <VLKButton
                  size="sm" variant="outline" className="mt-3"
                  onClick={async () => {
                    try {
                      await navigator.clipboard?.writeText(qrString);
                      toast.success("Copied to clipboard");
                    } catch {
                      toast.error("Copy blocked — select and copy manually");
                    }
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />Copy payload
                </VLKButton>
              </div>
            )}
          </VLKCardContent>
        </VLKCard>

        <VLKCard>
          <VLKCardHeader><VLKCardTitle>Verify</VLKCardTitle></VLKCardHeader>
          <VLKCardContent>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <VLKLabel>Payload</VLKLabel>
                <VLKInput
                  data-testid={IDS.qrVerifyInput}
                  value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  placeholder="qr_XXXX|identity_verification|2026-…"
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <VLKLabel>Signature</VLKLabel>
                <VLKInput
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="hex signature"
                  className="font-mono text-xs"
                />
              </div>
              <VLKButton data-testid={IDS.qrVerifyBtn} onClick={verify} loading={verifyLoading} disabled={!payload || !signature} className="w-full">
                <ShieldCheck className="h-4 w-4" />Verify token
              </VLKButton>
              {qr && (
                <VLKButton
                  size="sm" variant="ghost"
                  onClick={() => { setPayload(qr.payload); setSignature(qr.signature); }}
                  className="w-full"
                >Autofill from generator</VLKButton>
              )}
              {verifyResult && (
                <div className={"p-3 rounded-sm border text-sm font-mono " + (verifyResult.ok ? "border-success/30 bg-success/5 text-success" : "border-destructive/30 bg-destructive/5 text-destructive")}>
                  {verifyResult.ok
                    ? `✓ Verified · purpose=${verifyResult.data.purpose}`
                    : `✗ ${verifyResult.error}`}
                </div>
              )}
            </div>
          </VLKCardContent>
        </VLKCard>
      </div>
    </div>
  );
}
